<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { ref, computed, watch, onMounted } from "vue"
import { useI18n } from "vue-i18n"
import { useDebounceFn } from "@vueuse/core"
import { inject } from "vue"
import { formatDate, formatIdSource } from "@core/helpers"
import { useFetchWrapper, useRedirect } from "@core/composables"
import { useAuthStore } from "@core/stores/auth.store"
import { storeToRefs } from "pinia"
import type { ColumnConfig } from "@types"
import type { Services } from "@services"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            title: "Sources de facturation",
            search: "Rechercher une source...",
            no_sources: "Aucune source de facturation trouvée",
            columns: {
                source: "Source",
                type: "Type",
                added_date: "Date d'ajout",
                state: "État",
                billing: "Facturation ce mois-ci",
                folder: "Dossier",
                building: "Bâtiment associé",
                owner: "Titulaire",
            },
            state: {
                active: "Actif",
                removed: "Supprimé",
            },
            state_tooltip_title: "Date de suppression",
            billing: {
                yes: "Oui",
                no: "Non",
            },
            type_filter: {
                types: "Types",
                electrique: "Électrique",
                gaz: "Gaz",
                rcu: "RCU",
                eau: "Eau",
                capteurs: "Capteurs",
                snc: "SNC",
            },
            owner_from_owner: "{details}",
        },
    },
})

// ================================
// TYPES & INTERFACES
// ================================
type BillingSource = {
    _id_source: string
    idsource: string
    idtype: string
    relations?: Array<{
        createdAt: string
        dismissed: boolean
        dismissedAt?: string
    }>
    buildings?: Array<{
        _id_building: string
        address?: string
        folders?: Array<{
            _id_folder: string
            name: string
        }>
    }>
    owners?: Array<{
        type: "company" | "user"
        company?: { name: string }
        user?: { firstname: string; lastname: string }
    }>
}

type FormattedBillingSource = BillingSource & {
    addedDate: string
    state: { state: boolean; date?: string }
    /** Valeur booléenne pour le filtre BaseTable (true = actif, false = supprimé) */
    stateFilterValue: boolean
    /** Groupe de type pour le filtre (Électrique, Gaz, RCU, Eau si bêta, Capteurs, SNC) */
    typeFilterValue: string
    billing: boolean
    folder: { _id_folder: string; name: string } | null
    building: { _id_building: string; address?: string } | null
    owner: { type: string; details: string } | null
}

// ================================
// PROPS
// ================================
/** En mode true, le composant est dans un conteneur à hauteur limitée (ex. dialog) : header/recherche fixes, seul le tableau scrolle */
const props = withDefaults(defineProps<{ embedded?: boolean }>(), {
    embedded: false,
})

// ================================
// SERVICES & STORES
// ================================
const services = inject<Services>("services")!
const authStore = useAuthStore()
const { companyBetaFeatures, companyId } = storeToRefs(authStore)
const { redirectWrapper } = useRedirect()

// ================================
// STATE
// ================================
const sources = ref<BillingSource[]>([])
const total = ref(0)

const pagination = ref({
    page: 1,
    limit: 10,
})

const search = ref("")
const filtersSelected = ref({
    billing: "",
    state: "",
    idtype: "all",
})

// ================================
// COMPUTED PROPERTIES
// ================================

/**
 * Bêta-feature entreprise : propose le filtre type « Eau » (`eaubordeaux` côté API).
 */
const getHasEauBordeauxBeta = computed(
    () => companyBetaFeatures.value?.eaubordeaux === true
)

/**
 * Formate les sources avec les données calculées
 */
const getSourcesFormatted = computed<FormattedBillingSource[]>(() => {
    return sources.value.map((source) => {
        const state = getState(source)
        return {
            ...source,
            addedDate: getAddedDate(source),
            state,
            stateFilterValue: state.state,
            typeFilterValue: getTypeFilterValue(source.idtype),
            billing: isBillingThisMonth(source),
            folder: getFolder(source),
            building: getBuilding(source),
            owner: getOwner(source),
        }
    })
})

/**
 * Configuration des colonnes pour BaseTable
 */
const billingSourceColumns = computed<ColumnConfig[]>(() => {
    const typeFilterOptions: Array<{ label: string; value: string }> = [
        { label: t("type_filter.electrique"), value: "Électrique" },
        { label: t("type_filter.gaz"), value: "Gaz" },
        { label: t("type_filter.rcu"), value: "RCU" },
    ]
    if (getHasEauBordeauxBeta.value) {
        typeFilterOptions.push({ label: t("type_filter.eau"), value: "Eau" })
    }
    typeFilterOptions.push(
        { label: t("type_filter.capteurs"), value: "Capteurs" },
        { label: t("type_filter.snc"), value: "SNC" }
    )
    return [
        {
            field: "idsource",
            header: t("columns.source"),
            sortable: false,
            style: { width: "15rem" },
        },
        {
            field: "typeFilterValue",
            header: t("columns.type"),
            sortable: false,
            style: { width: "10rem" },
            filterable: true,
            filterOptions: typeFilterOptions,
        },
        {
            field: "addedDate",
            header: t("columns.added_date"),
            sortable: false,
            style: { width: "7rem" },
        },
        {
            field: "stateFilterValue",
            header: t("columns.state"),
            sortable: false,
            style: { width: "7rem" },
            filterable: true,
            filterOptions: [
                { label: t("state.active"), value: true },
                { label: t("state.removed"), value: false },
            ],
        },
        {
            field: "billing",
            header: t("columns.billing"),
            sortable: false,
            style: { width: "12rem" },
            filterable: true,
            filterOptions: [
                { label: t("billing.yes"), value: true },
                { label: t("billing.no"), value: false },
            ],
        },
        {
            field: "folder",
            header: t("columns.folder"),
            sortable: false,
            style: { width: "14rem" },
        },
        {
            field: "building",
            header: t("columns.building"),
            sortable: false,
            style: { width: "18rem" },
        },
        {
            field: "owner",
            header: t("columns.owner"),
            sortable: false,
            style: { width: "14rem" },
        },
    ]
})

// ================================
// FETCH DATA
// ================================

/**
 * Récupère les sources de facturation depuis l'API
 */
const { fetch: fetchSources, isFetching: fetchState } = useFetchWrapper(
    async () => {
        try {
            const response = await services.$getBillingSource({
                page: pagination.value.page,
                limit: pagination.value.limit,
                search: search.value || null,
                status: filtersSelected.value.state as
                    | ""
                    | "active"
                    | "removed",
                date: filtersSelected.value.billing as
                    | ""
                    | "current_month"
                    | "not_current_month",
                idtype: filtersSelected.value.idtype,
            })

            if (typeof response === "number") {
                total.value = response
                return
            }

            const { data } = response

            if (total.value === 0) {
                const countResponse = await services.$getBillingSource({
                    search: search.value || null,
                    count: true,
                    status: filtersSelected.value.state as
                        | ""
                        | "active"
                        | "removed",
                    date: filtersSelected.value.billing as
                        | ""
                        | "current_month"
                        | "not_current_month",
                    idtype: filtersSelected.value.idtype,
                })
                if (typeof countResponse === "number") {
                    total.value = countResponse
                }
            }

            sources.value = data
        } catch (err) {
            console.error("Error fetching billing sources:", err)
        }
    }
)

// ================================
// HELPER FUNCTIONS
// ================================

/**
 * Récupère la relation courante d'une source (triée par date puis dismissed)
 */
function getCurrentRelation(source: BillingSource) {
    const relations = [...(source?.relations || [])]

    if (relations.length === 0) {
        return undefined
    }

    // Trie par date de création (plus récent en premier)
    relations.sort(
        (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    )

    // Trie par statut (non dismissed en premier)
    relations.sort((a, b) => {
        if (a.dismissed && !b.dismissed) return 1
        if (!a.dismissed && b.dismissed) return -1
        return 0
    })

    return relations[0]
}

/**
 * Retourne le libellé de groupe pour un idtype (pour le filtre colonne Type).
 * Aligné sur les options du legacy : Électrique (pdl, elecsavoie), Gaz (pce, regaz), etc.
 */
function getTypeFilterValue(idtype: string): string {
    const groupByType: Record<string, string> = {
        pdl: "Électrique",
        elecsavoie: "Électrique",
        pce: "Gaz",
        regaz: "Gaz",
        cpcu: "RCU",
        dalkia: "RCU",
        eaubordeaux: "Eau",
        iot: "Capteurs",
        snc: "SNC",
    }
    return groupByType[idtype] ?? idtype
}

/**
 * Récupère la date d'ajout formatée
 */
function getAddedDate(source: BillingSource): string {
    const relation = getCurrentRelation(source)
    if (!relation) return ""
    return formatDate(relation.createdAt, "DD/MM/YYYY", "-")
}

/**
 * Récupère l'état de la source (actif ou retiré avec date)
 */
function getState(source: BillingSource): {
    state: boolean
    date?: string
} {
    const relation = getCurrentRelation(source)
    if (!relation) {
        // Si pas de relation, on considère la source comme active par défaut
        return {
            state: true,
        }
    }
    if (relation.dismissed) {
        return {
            state: false,
            date: relation.dismissedAt
                ? formatDate(relation.dismissedAt, "DD/MM/YYYY", "-")
                : undefined,
        }
    } else {
        return {
            state: true,
        }
    }
}

/**
 * Vérifie si la source est facturée ce mois
 */
function isBillingThisMonth(source: BillingSource): boolean {
    const relation = getCurrentRelation(source)
    if (!relation) return false
    if (!relation.dismissed) return true

    // Vérifie si la date de création est dans la période de facturation courante
    const currentOffer = authStore.getCurrentOffer ?? null
    if (!currentOffer?.current_start_period_monthly) return false

    const relationDate = new Date(relation.createdAt)
    const startPeriod = new Date(currentOffer.current_start_period_monthly)
    const endPeriod = currentOffer.current_end_period_monthly
        ? new Date(currentOffer.current_end_period_monthly)
        : new Date()

    return relationDate >= startPeriod && relationDate <= endPeriod
}

/**
 * Récupère le dossier de la source
 */
function getFolder(source: BillingSource) {
    if (!source?.buildings?.length || !source.buildings[0].folders?.length) {
        return null
    }

    return source.buildings[0].folders[0]
}

/**
 * Récupère le bâtiment de la source
 */
function getBuilding(source: BillingSource) {
    if (!source?.buildings?.length) {
        return null
    }
    return source.buildings[0]
}

/**
 * Récupère le titulaire de la source
 */
function getOwner(source: BillingSource) {
    if (!source?.owners?.length) {
        return null
    }

    const owner = source.owners[0]
    if (owner.type === "company") {
        return {
            type: owner.type,
            details: owner.company?.name || "",
        }
    } else {
        return {
            type: owner.type,
            details:
                `${owner.user?.firstname || ""} ${owner.user?.lastname || ""}`.trim(),
        }
    }
}

// ================================
// METHODS
// ================================

/**
 * Map groupe de type (option filtre) → idtype API (un représentant)
 */
const TYPE_GROUP_TO_IDTYPE: Record<string, string> = {
    Électrique: "pdl",
    Gaz: "pce",
    RCU: "cpcu",
    Eau: "eaubordeaux",
    Capteurs: "iot",
    SNC: "snc",
}

/**
 * À partir des filtres du tableau (emit BaseTable), met à jour filtersSelected et refetch
 * Le fetch est debouncé pour permettre de sélectionner plusieurs filtres avant de charger
 */
function onTableFiltersChange(
    tableFilters: Record<string, { value: unknown }>
) {
    const stateVal = tableFilters.stateFilterValue?.value
    if (Array.isArray(stateVal) && stateVal.length === 1) {
        filtersSelected.value.state =
            stateVal[0] === true ? "active" : "removed"
    } else {
        filtersSelected.value.state = ""
    }

    const billingVal = tableFilters.billing?.value
    if (Array.isArray(billingVal) && billingVal.length === 1) {
        filtersSelected.value.billing =
            billingVal[0] === true ? "current_month" : "not_current_month"
    } else {
        filtersSelected.value.billing = ""
    }

    const typeVal = tableFilters.typeFilterValue?.value
    if (Array.isArray(typeVal) && typeVal.length === 1 && typeVal[0]) {
        filtersSelected.value.idtype =
            TYPE_GROUP_TO_IDTYPE[String(typeVal[0])] ?? "all"
    } else {
        filtersSelected.value.idtype = "all"
    }

    pagination.value.page = 1
    total.value = 0
    // Ne pas vider sources : on garde les données affichées pendant le fetch
    // pour éviter le skeleton initial, le loading overlay suffit
    debouncedFetchSources()
}

/**
 * Refetch des sources debouncé (filtres tableau) pour éviter des requêtes à chaque changement
 */
const debouncedFetchSources = useDebounceFn(() => {
    fetchSources()
}, 400)

/**
 * Retire le filtre type Eau (`eaubordeaux`) si la bêta-feature n'est pas active pour l'entreprise.
 */
watch(
    [getHasEauBordeauxBeta, companyId],
    () => {
        if (!companyId.value) return
        if (
            !getHasEauBordeauxBeta.value &&
            filtersSelected.value.idtype === "eaubordeaux"
        ) {
            filtersSelected.value.idtype = "all"
            debouncedFetchSources()
        }
    },
    { immediate: true }
)

/**
 * Gère la recherche avec debounce
 */
const onSearch = useDebounceFn((value: string) => {
    search.value = value
    pagination.value.page = 1
    total.value = 0
    sources.value = []
    fetchSources()
}, 1000)

/**
 * Gère le changement de page du tableau (pagination côté serveur)
 * @param event - Event PrimeVue { first, rows }
 */
function onPage(event: { first: number; rows: number }) {
    pagination.value.page = Math.floor(event.first / event.rows) + 1
    pagination.value.limit = event.rows
    fetchSources()
}

/**
 * Gère le clic sur une source pour naviguer vers la page de la source
 * @param sourceId - Identifiant de la source
 */
function onClickSource(sourceId: string) {
    redirectWrapper("/source/:_id_source/data", { _id_source: sourceId })
}

/**
 * Gère le clic sur un dossier pour naviguer vers la page du dossier
 * @param folderId - Identifiant du dossier
 */
function onClickFolder(folderId: string) {
    redirectWrapper("/folder/:_id_folder", {
        _id_folder: folderId,
        _blank: true,
    })
}

/**
 * Gère le clic sur un bâtiment pour naviguer vers la page du bâtiment
 * @param buildingId - Identifiant du bâtiment
 */
function onClickBuilding(buildingId: string) {
    redirectWrapper("/building/:_id_building/data", {
        _id_building: buildingId,
        _blank: true,
    })
}

// ================================
// EXPOSE (pour usage dans CompanyBillingSourceDialog)
// ================================
defineExpose({
    total,
})

// ================================
// LIFECYCLE
// ================================

onMounted(() => {
    fetchSources()
})
</script>

<template>
    <div
        class="company-billing-source-list flex flex-col gap-8 pb-10"
        :class="{
            'flex-1 min-h-0 overflow-hidden': props.embedded,
        }"
    >
        <!-- ================================ -->
        <!-- RECHERCHE -->
        <!-- ================================ -->
        <div
            class="company-billing-source-list__search max-w-md mx-auto w-full"
        >
            <BaseInputWrapper input-id="search-billing-source">
                <template #prefix>
                    <BaseInputGroupAddon>
                        <BaseIcon icon="pi pi-search" />
                    </BaseInputGroupAddon>
                </template>
                <BaseInputText
                    id="search-billing-source"
                    v-model="search"
                    :placeholder="t('search')"
                    fluid
                    @update:model-value="onSearch"
                />
            </BaseInputWrapper>
        </div>

        <!-- ================================ -->
        <!-- TABLEAU (seule zone scrollable en mode embedded) -->
        <!-- ================================ -->
        <div
            class="company-billing-source-list__table flex flex-col min-h-0"
            :class="
                props.embedded
                    ? 'flex-1 overflow-hidden'
                    : 'max-h-[70vh] overflow-hidden'
            "
        >
            <!-- Tableau (toujours affiché, loading overlay pendant le fetch) -->
            <div
                class="overflow-auto overflow-x-auto min-h-0"
                :class="props.embedded ? 'flex-1' : ''"
            >
                <BaseTable
                    data-key="_id_source"
                    :columns="billingSourceColumns"
                    :value="getSourcesFormatted"
                    :total-records="total"
                    :lazy="true"
                    :first="(pagination.page - 1) * pagination.limit"
                    :loading="fetchState"
                    paginator
                    :rows="pagination.limit"
                    :rows-per-page-options="[10, 25, 50]"
                    scrollable
                    :empty-message="t('no_sources')"
                    class="w-full"
                    @page="onPage"
                    @update:filters="onTableFiltersChange"
                >
                    <template #cell_idsource="{ data }">
                        <div class="flex items-center gap-2 overflow-hidden">
                            <BaseSourceIcon
                                :type="data.idtype"
                                size="24"
                                class="flex-shrink-0"
                            />
                            <span
                                v-if="data.state.state"
                                class="text-primary hover:underline cursor-pointer overflow-hidden text-ellipsis whitespace-nowrap block"
                                @click="onClickSource(data._id_source)"
                            >
                                {{ formatIdSource(data.idsource, data.idtype) }}
                            </span>
                            <span
                                v-else
                                class="min-w-0 overflow-hidden text-ellipsis whitespace-nowrap block"
                            >
                                {{ formatIdSource(data.idsource, data.idtype) }}
                            </span>
                        </div>
                    </template>

                    <template #cell_typeFilterValue="{ data }">
                        {{ data.idtype?.toUpperCase() }}
                    </template>

                    <template #cell_addedDate="{ data }">
                        {{ data.addedDate }}
                    </template>

                    <template #cell_stateFilterValue="{ data }">
                        <span v-if="data.state.state">
                            {{ t("state.active") }}
                        </span>
                        <span v-else class="inline-flex items-center gap-1">
                            {{ t("state.removed") }}
                            <BaseTooltipMessage
                                v-if="data.state.date"
                                :title="t('state_tooltip_title')"
                                :content="data.state.date"
                                position="top"
                            />
                        </span>
                    </template>

                    <template #cell_billing="{ data }">
                        {{ data.billing ? t("billing.yes") : t("billing.no") }}
                    </template>

                    <template #cell_folder="{ data }">
                        <span
                            v-if="data.folder?._id_folder"
                            class="text-primary hover:underline cursor-pointer"
                            @click="onClickFolder(data.folder._id_folder)"
                        >
                            {{ data.folder.name }}
                        </span>
                    </template>

                    <template #cell_building="{ data }">
                        <span
                            v-if="data.building?.address"
                            class="text-primary hover:underline cursor-pointer"
                            @click="onClickBuilding(data.building._id_building)"
                        >
                            {{ data.building.address }}
                        </span>
                    </template>

                    <template #cell_owner="{ data }">
                        <span v-if="data.owner">
                            {{
                                t("owner_from_owner", {
                                    details: data.owner.details,
                                })
                            }}
                        </span>
                    </template>

                    <template #empty>
                        <BaseMessage severity="info" class="w-full">
                            {{ t("no_sources") }}
                        </BaseMessage>
                    </template>
                </BaseTable>
            </div>
        </div>
    </div>
</template>
